import { useEffect, useState } from 'react';
import AuthUser from './AuthUser';
import toast, { Toaster } from "react-hot-toast";
import moment from 'moment';
import TaskSkeleton from './TaskSkeleton';

export default function AntivirusReport() {
    const { http } = AuthUser();
    const [reportData, setReportData] = useState([]);
    const [staffList, setStaffList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [expandedStaff, setExpandedStaff] = useState({});
    
    // Filters
    const [filterStaff, setFilterStaff] = useState('');
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');

    useEffect(() => {
        fetchStaffList();
        fetchReport();
    }, []);

    const fetchStaffList = async () => {
        try {
            const response = await http.get('/antivirus_report/staff-list');
            if (response.data.success) {
                setStaffList(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching staff list:", error);
        }
    };

    const fetchReport = async () => {
        setIsLoading(true);
        try {
            const params = {};
            if (filterStaff) params.staff_id = filterStaff;
            if (filterStartDate) params.start_date = filterStartDate;
            if (filterEndDate) params.end_date = filterEndDate;

            const response = await http.get('/antivirus_report/staff', { params });
            if (response.data.success) {
                setReportData(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching report:", error);
            toast.error("Error loading report");
        } finally {
            setIsLoading(false);
        }
    };

    const handleApplyFilters = () => {
        fetchReport();
    };

    const handleClearFilters = () => {
        setFilterStaff('');
        setFilterStartDate('');
        setFilterEndDate('');
        setTimeout(() => fetchReport(), 100);
    };

    const toggleStaffDetails = (staffId) => {
        setExpandedStaff(prev => ({
            ...prev,
            [staffId]: !prev[staffId]
        }));
    };

    const getTotalRequests = () => {
        return reportData.reduce((sum, staff) => sum + staff.total_requests, 0);
    };

    return (
        <div className="container-fluid">
            <Toaster />
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header">
                            <h3 className="card-title">Antivirus Request Report - By Staff</h3>
                        </div>
                        <div className="card-body">
                            {/* Filters */}
                            <div className="row mb-4">
                                <div className="col-md-3">
                                    <label className="form-label">Filter by Staff</label>
                                    <select
                                        className="form-select"
                                        value={filterStaff}
                                        onChange={(e) => setFilterStaff(e.target.value)}
                                    >
                                        <option value="">All Staff</option>
                                        {staffList.map((staff) => (
                                            <option key={staff.id} value={staff.id}>
                                                {staff.name} {staff.last_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">Start Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={filterStartDate}
                                        onChange={(e) => setFilterStartDate(e.target.value)}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">End Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={filterEndDate}
                                        onChange={(e) => setFilterEndDate(e.target.value)}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">&nbsp;</label>
                                    <div>
                                        <button
                                            className="btn btn-sm btn-primary me-2"
                                            onClick={handleApplyFilters}
                                        >
                                            <i className="fas fa-filter me-1"></i> Apply
                                        </button>
                                        <button
                                            className="btn btn-sm btn-secondary"
                                            onClick={handleClearFilters}
                                        >
                                            <i className="fas fa-times me-1"></i> Clear
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Summary Cards */}
                            <div className="row mb-4">
                                <div className="col-md-4">
                                    <div className="card bg-primary text-white">
                                        <div className="card-body">
                                            <h5 className="card-title">Total Staff</h5>
                                            <h2>{reportData.length}</h2>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card bg-success text-white">
                                        <div className="card-body">
                                            <h5 className="card-title">Total Requests</h5>
                                            <h2>{getTotalRequests()}</h2>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card bg-info text-white">
                                        <div className="card-body">
                                            <h5 className="card-title">Average per Staff</h5>
                                            <h2>{reportData.length > 0 ? (getTotalRequests() / reportData.length).toFixed(1) : 0}</h2>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Report Table */}
                            {isLoading ? (
                                <TaskSkeleton />
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-bordered table-hover">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: '40px' }}></th>
                                                <th>Staff Name</th>
                                                <th>Email</th>
                                                <th>Total Requests</th>
                                                <th>First Request</th>
                                                <th>Last Request</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reportData.length > 0 ? (
                                                reportData.map((staff) => (
                                                    <>
                                                        <tr key={staff.staff_id}>
                                                            <td>
                                                                <button
                                                                    className="btn btn-sm btn-link"
                                                                    onClick={() => toggleStaffDetails(staff.staff_id)}
                                                                    title="View Details"
                                                                >
                                                                    <i className={`fas fa-chevron-${expandedStaff[staff.staff_id] ? 'down' : 'right'}`}></i>
                                                                </button>
                                                            </td>
                                                            <td>
                                                                <strong>{staff.staff_name}</strong>
                                                            </td>
                                                            <td>{staff.staff_email}</td>
                                                            <td>
                                                                <span className="badge bg-primary" style={{ fontSize: '14px' }}>
                                                                    {staff.total_requests}
                                                                </span>
                                                            </td>
                                                            <td>{moment(staff.first_request_date).format('DD/MM/YYYY')}</td>
                                                            <td>{moment(staff.last_request_date).format('DD/MM/YYYY')}</td>
                                                        </tr>
                                                        {expandedStaff[staff.staff_id] && (
                                                            <tr>
                                                                <td colSpan="6" style={{ backgroundColor: '#f8f9fa' }}>
                                                                    <div className="p-3">
                                                                        <h6 className="mb-3"><strong>Request Details</strong></h6>
                                                                        <div className="table-responsive">
                                                                            <table className="table table-sm table-striped">
                                                                                <thead>
                                                                                    <tr>
                                                                                        <th>Date</th>
                                                                                        <th>Type</th>
                                                                                        <th>Product</th>
                                                                                        <th>Company</th>
                                                                                        <th>Username</th>
                                                                                        <th>Contact</th>
                                                                                        <th>Activated On</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {staff.requests.map((request) => (
                                                                                        <tr key={request.id}>
                                                                                            <td>{moment(request.created_at).format('DD/MM/YYYY')}</td>
                                                                                            <td>
                                                                                                <span className={`badge ${request.type_request === 'Company' ? 'bg-info' : 'bg-secondary'}`}>
                                                                                                    {request.type_request || '-'}
                                                                                                </span>
                                                                                            </td>
                                                                                            <td>
                                                                                                {request.antivirus_master ? (
                                                                                                    <div>
                                                                                                        <small><strong>{request.antivirus_master.type_name}</strong></small>
                                                                                                        <br />
                                                                                                        <small className="text-muted">{request.antivirus_master.type_year}</small>
                                                                                                    </div>
                                                                                                ) : '-'}
                                                                                            </td>
                                                                                            <td>{request.company_name || '-'}</td>
                                                                                            <td>{request.username || '-'}</td>
                                                                                            <td>{request.contact_num || '-'}</td>
                                                                                            <td>{request.antivirus_activated_on ? moment(request.antivirus_activated_on).format('DD/MM/YYYY') : '-'}</td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="6" className="text-center">No report data found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

